import React, { useState, useEffect } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { useDispatch, useSelector } from "react-redux"
import {
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import { ref, get } from "firebase/database"
import { useHistory } from "react-router-dom"

import useSlots from "./checkout/useSlots"
import { sdk9db } from "../../firebase"
import { dateAndTimeToTimestamp } from "../../utils/dateUtils"
import { setTransientSlotId } from "../../actions/lessonActions"
import { setSnackbar } from "../../actions/globalUiActions"
import { lessonTypes } from "../../content"
import { Instructor, LessonTypeName } from "../../types"
import InstructorSelect from "./InstructorSelect"
import { DEFAULT_TIMEZONE } from "../../config"
import makeSlug from "../../utils/makeSlug"
import { getLocationPricesFactory } from "../../utils/locationUtils"
import { track } from "../../utils/analytics"
import { RootState } from "../../store/configureStore"
import { useCouponsContext } from "../CouponsProvider"
import LessonTypeSelect from "./LessonTypeSelect"
import ConfirmationDialog from "./ConfirmationDialog"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const DATE_FORMAT = "YYYY-MM-DD"
const TIME_FORMAT = "HH:mm"

const CustomTransientSlot = () => {
  const couponState = useCouponsContext()
  const history = useHistory()
  const dispatch = useDispatch()
  const { createTransientSlot } = useSlots()
  const {
    location: { searchGeocode, searchAddress, searchCity, searchState },
    search: { meetingPoints },
    firebase: { profile },
  } = useSelector((state: RootState) => state)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [date, setDate] = useState(
    dayjs().tz(DEFAULT_TIMEZONE).format(DATE_FORMAT),
  )
  const [startTime, setStartTime] = useState(
    dayjs().hour(9).startOf("hour").format(TIME_FORMAT),
  )
  const [lessonTypeLabel, setLessonTypeLabel] = useState(
    lessonTypes.standard.label,
  )
  const [lessonTypeDurationMinutes, setLessonTypeDurationMinutes] = useState(
    lessonTypes.standard.durationMinutes,
  )
  const [instructor, setInstructor] = useState<Instructor | null>(null)
  const [typeEdited, setTypeEdited] = useState(false)
  const [lessonType, setLessonType] = useState<LessonTypeName>(
    LessonTypeName.Standard,
  )
  const [selectedMeetingPointId, setSelectedMeetingPointId] = useState(
    meetingPoints ? meetingPoints[0].meetingPointId : null,
  )
  const [isShareable, setIsShareable] = useState(false)
  const [showShortLessonWarning, setShowShortLessonWarning] = useState(false)

  useEffect(() => {
    if (meetingPoints && !selectedMeetingPointId) {
      setSelectedMeetingPointId(meetingPoints[0].meetingPointId)
    }
  }, [meetingPoints, selectedMeetingPointId])

  const handleLessonTypeChange = (newLessonType: LessonTypeName) => {
    setLessonType(newLessonType)
    if (!typeEdited) {
      // Only auto-update label and duration if user hasn't manually edited them
      setLessonTypeLabel(lessonTypes[newLessonType].label)
      setLessonTypeDurationMinutes(lessonTypes[newLessonType].durationMinutes)
    }
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypeEdited(true)
    setLessonTypeLabel(e.target.value)
  }

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypeEdited(true)
    setLessonTypeDurationMinutes(parseInt(e.target.value, 10))
  }

  const handleSubmitConfirmed = () => {
    const selectedMeetingPoint = () => {
      if (meetingPoints) {
        return meetingPoints.filter(
          (point) => point.meetingPointId === selectedMeetingPointId,
        )[0]
      }
      return null
    }

    const pickupAddress = selectedMeetingPoint()?.address || ""
    const meetingPointId = selectedMeetingPoint()?.meetingPointId || ""

    const timeZone = instructor?.timeZone || DEFAULT_TIMEZONE
    const dateObject = dayjs.tz(`${date}T12:00:00`, timeZone)
    const startTimeObject = dayjs.tz(startTime, TIME_FORMAT, timeZone)
    const endTimeObject = startTimeObject.add(
      lessonTypeDurationMinutes,
      "minute",
    )

    const startTimeMillis = dateAndTimeToTimestamp(dateObject, startTimeObject)
    const endTimeMillis = dateAndTimeToTimestamp(dateObject, endTimeObject)

    if (!instructor || !instructor.id) {
      dispatch(setSnackbar("You must select an instructor.", "error"))
      return
    }

    const startHour = startTimeObject.get("hour")
    const hourFrom = 6
    const hourTo = 22
    if (startHour < hourFrom || startHour >= hourTo) {
      dispatch(
        setSnackbar(
          `Cannot create a lesson outside business hours (${dayjs().hour(hourFrom).format("ha")} - ${dayjs().hour(hourTo).format("ha")}).`,
          "error",
        ),
      )
      return
    }

    // First, make sure this instructor doesn't already have a lesson during
    // this time.
    get(ref(sdk9db, `lessonSlotsV2/${instructor.id}`)).then(
      (lessonSlotsSnap) => {
        let overlapping = false
        lessonSlotsSnap.forEach((lessonSlotSnap) => {
          const lessonSlot = lessonSlotSnap.val()
          if (
            lessonSlot.details.endTime > startTimeMillis &&
            lessonSlot.details.startTime < endTimeMillis
          ) {
            // Already have a lesson booked during this time.
            overlapping = true
          }
        })

        if (overlapping) {
          dispatch(
            setSnackbar(
              "There is already a lesson booked at this time.",
              "error",
            ),
          )
        } else {
          try {
            // We need to set all the packages based on the hourly price we set.
            const durationMinutes = lessonTypeDurationMinutes
            getLocationPricesFactory(couponState, searchGeocode!).then(
              async (getLocationPrices) => {
                const packagesWithPrice = await getLocationPrices(
                  durationMinutes,
                  searchState!,
                )

                if (
                  packagesWithPrice &&
                  !packagesWithPrice[lessonType]?.length
                ) {
                  dispatch(
                    setSnackbar(
                      "No prices found for this lesson type in this location.",
                      "error",
                    ),
                  )
                  return
                }

                const price = packagesWithPrice?.[lessonType]?.[0]?.msrp ?? 0

                const slot = {
                  instructor: {
                    displayName:
                      instructor.displayName ||
                      `${instructor.firstName} ${instructor.lastName}`,
                    id: instructor.id,
                    portraitImage: instructor.portraitImage || "",
                    wallpaperImage: instructor.wallpaperImage || "",
                    userId: instructor.userId,
                    slackUsername: instructor.slackUsername || "",
                    timeZone: instructor.timeZone || DEFAULT_TIMEZONE,
                  },
                  startTime: startTimeMillis,
                  endTime: endTimeMillis,
                  locationId: makeSlug(searchCity),
                  searchAddress: searchAddress || "",
                  searchCity: searchCity || "",
                  searchState: searchState || "",
                  pickupAddress,
                  meetingPointId,
                  price,
                  lessonType: {
                    name: lessonType,
                    label: lessonTypeLabel,
                    durationMinutes,
                  },
                  updatedAt: new Date().valueOf(),
                  isCustom: true,
                  isShareable,
                }

                createTransientSlot(slot).then((slotId) => {
                  dispatch(setTransientSlotId(slotId))
                  track("Admin Created Custom Slot", {
                    date,
                    startTime,
                    instructorId: instructor?.id,
                  })
                  history.push(`/book-lesson/${slotId}/1`)
                })
              },
            )
          } catch {
            dispatch(
              setSnackbar(
                "Failed to retrieve location prices. Please try again.",
                "error",
              ),
            )
          }
        }
      },
    )
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const isRoadTest = lessonType.toLowerCase().includes("road-test")

    if (lessonTypeDurationMinutes <= 30 && !isRoadTest) {
      setShowShortLessonWarning(true)
      return
    }

    handleSubmitConfirmed()
  }

  const handleConfirmShortLesson = () => {
    setShowShortLessonWarning(false)
    handleSubmitConfirmed()
  }

  const handleCancelShortLesson = () => {
    setShowShortLessonWarning(false)
  }

  if (
    !profile?.isAdmin ||
    !selectedMeetingPointId ||
    !meetingPoints ||
    !searchAddress ||
    !searchCity
  )
    return null

  return (
    <div>
      <Button
        onClick={() => setDialogOpen(true)}
        color="primary"
        variant="outlined"
      >
        Create Custom Slot
      </Button>
      <Dialog open={dialogOpen} data-cy="custom-transient-dialog">
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <TextField
              select
              label="Pickup or Meeting Point"
              value={selectedMeetingPointId}
              onChange={(e) => setSelectedMeetingPointId(e.target.value)}
              margin="normal"
              data-cy="meeting-point-select"
              fullWidth
            >
              {meetingPoints.map((meetingPoint) => (
                <MenuItem
                  value={meetingPoint.meetingPointId}
                  key={meetingPoint.meetingPointId}
                  data-cy="meeting-point-select-item"
                >
                  {meetingPoint.meetingPointLabel}, {meetingPoint.address},{" "}
                  {meetingPoint.distanceMiles.toFixed(1)} mile(s) away
                </MenuItem>
              ))}
            </TextField>
            <LessonTypeSelect
              selectedLessonType={lessonType}
              onSelected={handleLessonTypeChange}
            />
            <TextField
              label="Date"
              type="date"
              name="date"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setDate(e.target.value)}
              value={date}
              margin="normal"
            />
            <br />
            <TextField
              label="Start Time"
              type="time"
              name="startTime"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              margin="normal"
            />
            <br />
            <TextField
              placeholder="120"
              type="number"
              name="durationMinutes"
              label="Lesson Duration (minutes)"
              onChange={handleDurationChange}
              value={lessonTypeDurationMinutes}
              margin="normal"
            />
            <br />
            <TextField
              placeholder="2-hour behind-the-wheel lesson"
              label="Lesson Label"
              name="lessonTypeLabel"
              onChange={handleLabelChange}
              value={lessonTypeLabel}
              margin="normal"
            />
            <br />
            <InstructorSelect
              selectedInstructorId={instructor?.id}
              onChangeCallback={(ins: Instructor) => setInstructor(ins)}
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  name="isShareable"
                  checked={isShareable}
                  onChange={(e) => setIsShareable(e.target.checked)}
                  color="primary"
                />
              }
              label="Is lesson shareable (Texas & Illinois only)?"
            />
            <br />
            <Button
              variant="contained"
              type="submit"
              color="primary"
              style={{ marginTop: 20 }}
              data-cy="custom-transient-create-button"
            >
              Create Slot
            </Button>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={showShortLessonWarning}
        callbackCancel={handleCancelShortLesson}
        callbackConfirm={handleConfirmShortLesson}
        title="Confirm Short Lesson Duration"
        message={
          "You've selected a short lesson duration (30 minutes or less) for a non-road test lesson type. Are you sure this is what you want?"
        }
        actionButtonLabel="Yes, continue"
      />
    </div>
  )
}

export default CustomTransientSlot
